import { useQuery } from '@tanstack/react-query';
import type { AIPatrolLevel } from './ai-patrol';
import { post } from './client';
import type {
  MedicationItem,
  MonitoringItem,
  PagedResult,
  PatientBasic,
  PatientHealth,
  PatientListItem,
} from './model';

export interface PatientListParams {
  pageNo?: number;
  pageSize?: number;
  keyword?: string;
  startDate?: string;
  endDate?: string;
}

export type PatientPoolFollowStatus = 'followed' | 'unfollowed';

export interface PatientPoolListParams {
  pageNo: number;
  pageSize: number;
  keyword?: string;
  followStatus?: PatientPoolFollowStatus;
}

export interface PatientPoolItem {
  id: string;
  name: string | null;
  phone: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  gender: string | null;
  birthDate: string | null;
  idCard: string | null;
  healthManagerId: string | null;
  healthManagerName: string | null;
  inMyPatients: boolean | null;
  // 专科医生姓名, 多位用顿号拼接, 没有时为空串
  doctorNames: string;
  // 方案状态: draft / published, 无方案为空串
  planStatus: string;
  planInConsultation: boolean;
}

export interface PatientPoolStats {
  totalCount: number;
  followedCount: number;
  unfollowedCount: number;
}

export interface PatientDetail {
  patient: PatientDetailBasic;
  metrics: PatientDetailMetrics;
  aiPatrol: PatientDetailAIPatrol[];
  aiPlan: PatientDetailAIPlan;
  chat: PatientDetailChat;
  subscriptions: PatientDetailSubscription[];
}

// 患者某类小程序通知的授权与推送情况; 长期模板的授权状态微信不提供查询,
// 只能看小程序上报的最近一次授权结果和服务端最近一次推送结果
export interface PatientDetailSubscription {
  templateId: string;
  name: string;
  // 为空表示小程序没有上报过 (旧版本小程序或从未请求过)
  authResult: 'accept' | 'reject' | 'ban' | 'filter' | 'switch_off' | null;
  authAt: string | null;
  // 为空表示还没有推送过; unauthorized = 微信返回 43101, 患者未授权或已关闭这类通知
  sendStatus: 'sent' | 'unauthorized' | 'failed' | null;
  sendError: string | null;
  sendAt: string | null;
}

export interface PatientDetailBasic {
  id: string;
  name: string | null;
  phone: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  gender: string | null;
  birthDate: string | null;
  bloodType: string | null;
  smoking: string | null;
  drinking: string | null;
  nation: string | null;
  healthManagerId: string | null;
  healthManagerName: string | null;
  inMyPatients: boolean | null;
  doctors: PatientDetailCareMember[];
}

export interface PatientDetailCareMember {
  id: string;
  name: string | null;
}

export interface PatientDetailMetrics {
  systolicBp: number | null;
  diastolicBp: number | null;
  fastingGlucose: number | null;
  totalCholesterol: number | null;
  heightCm: number | null;
  weightKg: number | null;
  bmi: number | null;
}

// 患者在各 AI 巡检集合里当前命中的结果
export interface PatientDetailAIPatrol {
  id: string;
  setKey: string;
  setName: string;
  level: AIPatrolLevel;
  levelName: string;
  rules: string[];
  evidenceAt: string | null;
  status: 'pending' | 'invited';
}

export interface PatientDetailAIPlan {
  available: boolean;
  id: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  diet: string[];
  // 医生设定的每日推荐摄入目标(千卡/天),null 表示跟随公式估算
  dietCalorieTarget: number | null;
  exercise: string[];
  nutrition: string[];
  tcmRegulation: string[];
  ownerId: string | null;
  ownerName: string | null;
  inConsultation: boolean;
  monitoring: MonitoringItem[];
  medication: MedicationItem[];
  pendingQuestionnaires: PatientDetailPendingQuestionnaire[];
  summary: string | null;
  executionSummary: PatientDetailAIPlanExecutionSummary | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PatientDetailPendingQuestionnaire {
  assignmentId: string;
  questionnaireId: string;
  title: string;
}

export interface PatientDetailAIPlanExecutionSummary {
  text: string;
  items: PatientDetailAIPlanExecutionItem[];
}

export interface PatientDetailAIPlanExecutionItem {
  category: string;
  label: string;
  completedCount: number;
  totalCount: number;
}

export interface PatientDetailChat {
  conversationId: string | null;
  recent24hCount: number;
  hasPendingReview: boolean;
  lastMessageAt: string | null;
  messages: PatientDetailChatMessage[];
}

export interface PatientDetailChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  attachments: PatientDetailChatAttachment[];
  reviewStatus: 'approved' | 'pending_review' | 'revised';
  createdAt: string;
}

export interface PatientDetailChatAttachment {
  objectId: string;
  name: string;
  fileType: string;
  size: number;
  url?: string;
}

export interface PatientArchive {
  patient: PatientArchiveBasic;
  metrics: PatientDetailMetrics;
  doctors: PatientDetailCareMember[];
  health: PatientArchiveHealth;
  peisExams: PatientArchivePeisExam[];
  his: PatientArchiveHis;
}

export interface PatientArchiveBasic {
  id: string;
  name: string | null;
  phone: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  gender: string | null;
  birthDate: string | null;
  bloodType: string | null;
  smoking: string | null;
  drinking: string | null;
  nation: string | null;
  healthManagerId: string | null;
  healthManagerName: string | null;
  inMyPatients: boolean | null;
}

export interface PatientArchiveHealth {
  diseases: PatientArchiveDisease[];
  surgeries: PatientArchiveSurgery[];
  family: PatientArchiveFamily[];
  allergies: PatientArchiveAllergy[];
}

export interface PatientArchiveDisease {
  id: string;
  diseaseCode: string;
  diseaseName: string;
  diagnosedDate: string | null;
  note: string | null;
  // 首次写入这一行的来源: 患者手动添加 / HIS 回填 / 五病筛查回写
  source: 'manual' | 'his' | 'questionnaire';
  createdAt: string;
}

export interface PatientArchiveSurgery {
  id: string;
  surgeryCode: string;
  surgeryName: string;
  surgeryDate: string | null;
  note: string | null;
  createdAt: string;
}

export interface PatientArchiveFamily {
  id: string;
  diseaseCode: string;
  diseaseName: string;
  relation: string;
  note: string | null;
  createdAt: string;
}

export interface PatientArchiveAllergy {
  id: string;
  allergenType: string;
  allergenName: string;
  severity: string;
  reaction: string | null;
  note: string | null;
  createdAt: string;
}

export interface PatientArchivePeisExam {
  id: string;
  peisId: string | null;
  studyId: string | null;
  name: string | null;
  gender: string | null;
  age: number | null;
  phone: string | null;
  examType: string | null;
  examTypeName: string | null;
  checkedAt: string | null;
  companyCode: string | null;
  projectName: string | null;
  items: string | null;
  completed: string | null;
  missing: string | null;
  summary: string | null;
  positiveText: string | null;
  advice: PatientArchiveAdvice[];
  labItems: PatientArchivePeisLabItem[];
  reports: PatientArchivePeisReport[];
}

export interface PatientArchiveAdvice {
  label: string;
  text: string | null;
}

export interface PatientArchivePeisLabItem {
  rowId: number;
  name: string | null;
  result: string | null;
  unit: string | null;
  reference: string | null;
  flag: number | null;
  checkedAt: string | null;
  doctor: string | null;
  auditor: string | null;
  auditAt: string | null;
}

export interface PatientArchivePeisReport {
  type: string;
  studyId: string;
  description: string | null;
  conclusion: string | null;
  doctor: string | null;
  studyAt: string | null;
  reportDoctor: string | null;
  reportAt: string | null;
}

export interface PatientArchiveHis {
  outpatients: PatientArchiveHisOutpatient[];
  inpatients: PatientArchiveHisInpatient[];
  diagnoses: PatientArchiveHisDiagnosis[];
  labReports: PatientArchiveHisLabReport[];
  examReports: PatientArchiveHisExamReport[];
  pathologyReports: PatientArchiveHisExamReport[];
  orders: PatientArchiveHisOrder[];
}

export interface PatientArchiveHisOutpatient {
  no: string;
  name: string;
  phone: string | null;
  visitedAt: string | null;
  department: string | null;
  // 接诊医生
  doctor: string | null;
  allergy: string | null;
  implants: string | null;
}

export interface PatientArchiveHisInpatient {
  no: string;
  name: string;
  phone: string | null;
  status: string | null;
  admittedAt: string | null;
  dischargedAt: string | null;
  admitDept: string | null;
  currentDept: string | null;
  dischargeDept: string | null;
  admitDiagName: string | null;
  dischargeDiagName: string | null;
  chiefComplaint: string | null;
  brief: string | null;
  treatPlan: string | null;
  diseaseHistory: string | null;
  surgeryHistory: string | null;
  drugAllergy: string | null;
  foodAllergy: string | null;
  // 主治医生
  attendDoctor: string | null;
  // 床位医生
  bedDoctor: string | null;
  totalCost: string | null;
}

export interface PatientArchiveHisDiagnosis {
  encounterType: string;
  workorder: string;
  code: string;
  type: string;
  name: string | null;
  isMajor: boolean;
  diagnosedAt: string | null;
}

export interface PatientArchiveHisLabReport {
  reportNo: string;
  inhospNo: string | null;
  outhospNo: string | null;
  name: string | null;
  category: string | null;
  sampleNo: string | null;
  reportAt: string | null;
  applyDept: string | null;
  applyDoctor: string | null;
  reportDoctor: string | null;
  diagnosis: string | null;
  note: string | null;
  items: PatientArchiveHisLabItem[];
}

export interface PatientArchiveHisLabItem {
  code: string;
  name: string | null;
  value: string | null;
  unit: string | null;
  reference: string | null;
  excessRange: string | null;
  normalFlag: string | null;
  critical: string | null;
  sampleType: string | null;
  note: string | null;
}

export interface PatientArchiveHisExamReport {
  reportNo: string;
  inhospNo: string | null;
  outhospNo: string | null;
  name: string | null;
  category: string | null;
  method: string | null;
  item: string | null;
  part: string | null;
  result: string | null;
  description: string | null;
  reportAt: string | null;
  applyDept: string | null;
  applyDoctor: string | null;
  reportDoctor: string | null;
  diagnosis: string | null;
  note: string | null;
}

export interface PatientArchiveHisOrder {
  orderNo: string;
  groupNo: string | null;
  inhospNo: string;
  category: string | null;
  name: string | null;
  type: string | null;
  itemName: string | null;
  diagnosis: string | null;
  orderedAt: string | null;
  beginAt: string | null;
  endAt: string | null;
  status: string | null;
  department: string | null;
  doctor: string | null;
  route: string | null;
  spec: string | null;
  dosage: string | null;
  frequency: string | null;
  amount: string | null;
  entrust: string | null;
  note: string | null;
}

export const patientKeys = {
  all: ['patient'] as const,
  list: (p: PatientListParams) => [...patientKeys.all, 'list', p] as const,
  poolList: (p: PatientPoolListParams) =>
    [...patientKeys.all, 'pool-list', p] as const,
  poolStats: () => [...patientKeys.all, 'pool-stats'] as const,
  detail: (id: string) => [...patientKeys.all, 'detail', id] as const,
  archive: (id: string) => [...patientKeys.all, 'archive', id] as const,
  basic: (id: string) => [...patientKeys.all, 'basic', id] as const,
  health: (id: string) => [...patientKeys.all, 'health', id] as const,
};

export const listPatients = (p: PatientListParams) =>
  post<PagedResult<PatientListItem>>('/patient/list', p);

export const listPatientPool = (p: PatientPoolListParams) =>
  post<PagedResult<PatientPoolItem>>('/patient/pool/list', p);

export const getPatientPoolStats = () =>
  post<PatientPoolStats>('/patient/pool/stats');

export const getPatientDetail = (id: string) =>
  post<PatientDetail>('/patient/detail', { accountId: id });

export const getPatientArchive = (id: string) =>
  post<PatientArchive>('/patient/archive', { accountId: id });

export const getPatientBasic = (id: string) =>
  post<PatientBasic>('/patient/basic', { accountId: id });

export const getPatientHealth = (id: string) =>
  post<PatientHealth>('/patient/health', { accountId: id });

export const usePatientListQuery = (p: PatientListParams) =>
  useQuery({
    queryKey: patientKeys.list(p),
    queryFn: () => listPatients(p),
  });

export const usePatientPoolListQuery = (p: PatientPoolListParams) =>
  useQuery({
    queryKey: patientKeys.poolList(p),
    queryFn: () => listPatientPool(p),
  });

export const usePatientPoolStatsQuery = () =>
  useQuery({
    queryKey: patientKeys.poolStats(),
    queryFn: getPatientPoolStats,
  });

export const usePatientDetailQuery = (id: string) =>
  useQuery({
    queryKey: patientKeys.detail(id),
    queryFn: () => getPatientDetail(id),
    enabled: !!id,
  });

export const usePatientArchiveQuery = (id: string) =>
  useQuery({
    queryKey: patientKeys.archive(id),
    queryFn: () => getPatientArchive(id),
    enabled: !!id,
  });

export const usePatientBasicQuery = (id: string) =>
  useQuery({
    queryKey: patientKeys.basic(id),
    queryFn: () => getPatientBasic(id),
    enabled: !!id,
  });

export const usePatientHealthQuery = (id: string) =>
  useQuery({
    queryKey: patientKeys.health(id),
    queryFn: () => getPatientHealth(id),
    enabled: !!id,
  });
